import type { APIDispatch } from '@/hooks/api';
import type {
  CreateUserResponse,
  LoadUsersResponse,
  PartialUser,
  User,
} from './types';
import type { AuthContext } from '@/context';
import type { UsersStore } from '.';
import { apiInvoke, shouldLoad } from '../helpers/load';
import { makePayloadRequestInit } from '@/lib/api';

export async function loadUsers(
  { loaded, dispatch }: UsersStore,
  { force, api, auth }: { force: boolean; api: APIDispatch; auth: AuthContext },
) {
  if (!shouldLoad(loaded.current, force, auth)) {
    return false;
  }

  loaded.current = true;
  const res = await apiInvoke<LoadUsersResponse>(api, '/user', auth);
  dispatch({ type: 'setUsers', users: res });

  return true;
}

export async function updateUser(
  { dispatch }: UsersStore,
  {
    api,
    auth,
    item,
  }: { api: APIDispatch; auth: AuthContext; item: PartialUser },
): Promise<boolean> {
  await apiInvoke(
    api,
    `/user/${item.id}`,
    auth,
    makePayloadRequestInit(item.data, 'PATCH'),
  );
  dispatch({ type: 'updateUser', user: item });

  return true;
}

export async function archiveUser(
  store: UsersStore,
  {
    api,
    auth,
    item,
  }: { api: APIDispatch; auth: AuthContext; item: PartialUser },
) {
  await updateUser(store, {
    api,
    auth,
    item: { id: item.id, data: { archived: true } },
  });
}

export async function createUser(
  { dispatch }: UsersStore,
  {
    api,
    auth,
    item,
    invite,
  }: { api: APIDispatch; auth: AuthContext; item: User; invite: boolean },
) {
  let path = `/user`;
  if (!invite) {
    path = `${path}?invite=false`;
  }

  const res = await api(
    path,
    auth.firebaseUser || null,
    makePayloadRequestInit(item.data, 'POST'),
  );
  if (!res.ok) {
    throw new Error(await getApiErrorMessage(res, 'Unable to create user.'));
  }

  const payload = (await res.json()) as CreateUserResponse;
  dispatch({ type: 'addUser', user: payload });
}

async function getApiErrorMessage(response: Response, fallback: string) {
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    const payload = (await response.json()) as { message?: string };

    return payload.message || fallback;
  }

  const text = (await response.text()).trim();

  return text || fallback;
}

export async function resendInvite({
  api,
  auth,
  item,
}: {
  api: APIDispatch;
  auth: AuthContext;
  item: User;
}) {
  await apiInvoke(
    api,
    `/user/${item.id}`,
    auth,
    makePayloadRequestInit({ app: 'mhai' }, 'PUT'),
  );
}
